// Parses the agent's free-form markdown answer into the named sections we
// want to surface in the UI (win condition, key interactions, mana base,
// sideboard guide, honorable mentions, exemplars). The raw answer is still
// shown verbatim in a <details> block so users can copy it unchanged — this
// parser just produces a structured view alongside it.
//
// The agent uses a mix of `## H2 headings` and `**Label:**` bold-prefix
// sections. We recognize both and normalize titles to a small set of
// canonical keys. Anything we don't recognize falls through to `other` so
// we still render useful `##` sections like "Why this deck wins", etc.

export interface ParsedSections {
  winCondition?: string;
  keyInteractions?: string;
  manaBase?: string;
  sideboardGuide?: string;
  strategy?: string;
  weaknesses?: string;
  meta?: string;
  honorableMentions?: string;
  exemplarsConsulted?: string;
  other: Array<{ title: string; body: string }>;
}

export function parseAnswerSections(answer: string): ParsedSections {
  // Strip fenced code blocks first so the Arena decklist isn't misread as a
  // section. The raw view keeps them for copy.
  const noCode = answer.replace(/```[\s\S]*?```/g, "");
  const lines = noCode.split("\n");

  type Boundary = {
    idx: number;
    kind: "h2" | "bold";
    title: string;
    inline?: string;
  };
  const bounds: Boundary[] = [];
  for (let i = 0; i < lines.length; i++) {
    const L = lines[i]!;
    const h2 = /^##\s+(.+?)\s*$/.exec(L);
    if (h2) {
      bounds.push({ idx: i, kind: "h2", title: h2[1]!.trim() });
      continue;
    }
    const bold = /^\*\*([^*]+?):\*\*(.*)$/.exec(L);
    if (bold) {
      bounds.push({
        idx: i,
        kind: "bold",
        title: bold[1]!.trim(),
        inline: bold[2]!.trim(),
      });
    }
  }

  interface Section {
    title: string;
    body: string;
    kind: "h2" | "bold";
  }
  const sections: Section[] = [];

  // Note: earlier versions synthesized a "Strategy" section from any
  // prose above the first H2. With prompt v6 the Arena fence is pinned
  // to the top of the answer and preamble is at most a 15-word opener,
  // so that synthesis now surfaces boilerplate ("Clean bill of health —
  // 60 main, 15 side, legal. Here's the list.") as a Strategy panel.
  // Removed deliberately; if the agent has actual strategic prose it
  // belongs under `## Win Condition` per v6's mandated order.

  for (let k = 0; k < bounds.length; k++) {
    const b = bounds[k]!;
    const end = k + 1 < bounds.length ? bounds[k + 1]!.idx : lines.length;
    const tail = lines.slice(b.idx + 1, end).join("\n").trim();
    const body =
      b.kind === "bold" && b.inline
        ? b.inline + (tail ? "\n" + tail : "")
        : tail;
    sections.push({ title: b.title, body, kind: b.kind });
  }

  const out: ParsedSections = { other: [] };
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");
  for (const s of sections) {
    const t = norm(s.title);
    if (!out.winCondition && /^wincondition/.test(t)) out.winCondition = s.body;
    else if (
      !out.keyInteractions &&
      /^(keyinteractions?|keysynergies?|keyline|synergies|gameplan)/.test(t)
    )
      out.keyInteractions = s.body;
    else if (!out.manaBase && /^mana(base|curve)/.test(t)) out.manaBase = s.body;
    // Require a "guide"/"ing"/"plan" suffix — bare `## Sideboard` is the
    // Arena decklist wrapper (see line 119's wrapper-skip list), not a
    // narrative section. Matching it here silently routed the Arena
    // sideboard contents into the Sideboard Guide panel and shoved the
    // real narrative under `other`, so the UI rendered a thin checksum
    // in place of multi-bullet matchup analysis.
    else if (!out.sideboardGuide && /^sideboard(guide|ing|plan)/.test(t))
      out.sideboardGuide = s.body;
    else if (!out.strategy && /^strategy/.test(t)) out.strategy = s.body;
    else if (!out.weaknesses && /^(real)?weakness/.test(t))
      out.weaknesses = s.body;
    else if (!out.meta && /^meta/.test(t)) out.meta = s.body;
    else if (!out.honorableMentions && /^honorablemention/.test(t))
      out.honorableMentions = s.body;
    else if (!out.exemplarsConsulted && /^exemplarsconsulted/.test(t))
      out.exemplarsConsulted = s.body;
    else if (s.kind === "h2" && s.body) {
      // Skip the `## Deck` / `## Sideboard` / `## Decklist` wrappers — the
      // parsed DeckView already renders those. Everything else from the H2
      // stream is surfaced as "other" so we don't silently drop content
      // (e.g. "Why this deck wins", "Archetypes I rejected").
      if (!/^(deck|decklist|sideboard|mainboardmathcheck)$/.test(t)) {
        out.other.push({ title: s.title, body: s.body });
      }
    }
  }
  return out;
}

/**
 * True if parse produced at least one recognized named section. Lets the
 * caller decide whether to bother rendering the sections panel or just
 * fall back to the raw view.
 */
export function hasAnyNamedSection(p: ParsedSections): boolean {
  return Boolean(
    p.winCondition ||
      p.keyInteractions ||
      p.manaBase ||
      p.sideboardGuide ||
      p.strategy ||
      p.weaknesses ||
      p.meta ||
      p.honorableMentions ||
      p.exemplarsConsulted ||
      p.other.length > 0,
  );
}
